/**
 * 표의 **열 맞춤** — 표가 놓인 상자가 좁으면 덜 중요한 열부터 뺀다(eric 2026-09-28
 * 「목록의 일부 열을 공간이 모자라면 숨겨야지」).
 *
 * 🔴 **재는 것은 표가 놓인 «상자»의 폭이다 — 뷰포트가 아니다.** 서랍이 열리면 같은 뷰포트에서
 *    목록 상자만 좁아진다. 미디어 쿼리 · 해상도 숫자를 쓰지 않는다(고정 레이아웃 정책).
 * 🔴 여기는 **순수 함수만** 둔다. 배치(폭 재기)는 `DataTable` 이 하고, 무엇을 뺄지는 이 함수가 정한다 —
 *    jsdom 은 배치를 안 하므로 시험은 이 함수에 폭을 주입해 건다(`columnFit.test.ts`).
 *
 * 규칙
 * - `priority` 가 **없는 열은 늘 보인다.** 표 전체에 하나도 없으면 아무것도 빠지지 않는다(종전 그대로 —
 *   장비 관점 콘솔 · 태블릿 · 모듈 앱의 표가 여기 해당한다)
 * - **숫자가 낮을수록 먼저 빠진다.** 같으면 오른쪽 열이 먼저
 * - 🔴 **첫 열은 `priority` 를 줘도 빠지지 않는다** — 줄을 식별하는 이름이고 키보드로 행을 여는
 *   진짜 `button` 이 그 칸에 있다(`DataTable` 주석). 상태 열은 호출부가 `priority` 를 안 줘서 남긴다
 *
 * ## 「줄인 꼴」 단계 (eric 2026-09-28 「줄여」)
 *
 * 빼기 전에 **줄일 수 있는 열은 먼저 줄인다**(`compactPriority`). 작업 ID(36자 UUID)가 앞 8자 + 「…」로
 * 줄면 그 폭만큼 다른 열이 살아남는다 — 서랍이 열려 좁을 때만 일어난다(넉넉하면 전체 그대로).
 * 단계는 한 줄로 선다: `fitSteps()` 가 「줄이기」와 「빼기」를 **같은 숫자 축**에 섞어 차례를 매긴다.
 * 같은 숫자면 줄이기가 먼저다(값이 남는 쪽이 먼저).
 * 🔴 첫 열도 줄일 수는 있다 — 빼지만 않는다(행을 여는 버튼은 줄인 꼴에서도 그 칸에 있다).
 */

/**
 * 폭을 비교할 때 봐주는 오차(px) — 부동소수 잡음만 넘긴다.
 * 🔴 1px 로 두었다가 **1px 넘친 표를 들어간다고 읽어 가로 스크롤 막대가 남았다**(1255×742 알람 실측).
 *    폭은 소수점까지 잰다(`getBoundingClientRect`). 넘침과 되돌림이 같은 부등식을 써서 왕복하지 않는다.
 */
export const FIT_TOLERANCE = 0.1

export interface FitColumn {
  key: string
  priority?: number | undefined
  /** 줄인 꼴로 바꾸는 차례. 안 주면 줄이지 않는다 */
  compactPriority?: number | undefined
}

/** 맞춤 한 단계 — 그 열을 줄이거나(`compact`) 뺀다(`hide`) */
export interface FitStep {
  key: string
  kind: 'compact' | 'hide'
}

/**
 * 맞춤 단계의 차례 — 먼저 일어나는 것이 앞. 숫자가 낮을수록 먼저, 같으면 줄이기가 먼저, 그다음 오른쪽 열이 먼저.
 * 빼기 단계는 `hideOrder()` 와 같은 규칙(첫 열 제외)이다.
 */
export function fitSteps(columns: readonly FitColumn[]): FitStep[] {
  const steps: { step: FitStep; rank: number; index: number }[] = []
  columns.forEach((column, index) => {
    if (column.compactPriority !== undefined) {
      steps.push({
        step: { key: column.key, kind: 'compact' },
        rank: column.compactPriority,
        index,
      })
    }
    if (index > 0 && column.priority !== undefined) {
      steps.push({ step: { key: column.key, kind: 'hide' }, rank: column.priority, index })
    }
  })
  return steps
    .sort(
      (a, b) =>
        a.rank - b.rank ||
        (a.step.kind === b.step.kind ? 0 : a.step.kind === 'compact' ? -1 : 1) ||
        b.index - a.index,
    )
    .map(({ step }) => step)
}

/** 앞 `applied` 단계를 적용한 결과 — 보일 열(원래 순서)과 줄인 열 · 뺀 열의 키 */
export function applyFit<C extends FitColumn>(
  columns: readonly C[],
  applied: number,
): { columns: C[]; compacted: string[]; hidden: string[] } {
  const done = fitSteps(columns).slice(0, Math.max(0, applied))
  const hidden = done.filter((step) => step.kind === 'hide').map((step) => step.key)
  const compacted = done
    .filter((step) => step.kind === 'compact' && !hidden.includes(step.key))
    .map((step) => step.key)
  const gone = new Set(hidden)
  return { columns: columns.filter((column) => !gone.has(column.key)), compacted, hidden }
}

/** 빠지는 차례 — 먼저 빠지는 열이 앞. 첫 열과 `priority` 없는 열은 들지 않는다 */
export function hideOrder(columns: readonly FitColumn[]): string[] {
  return columns
    .map((column, index) => ({ column, index }))
    .filter(({ column, index }) => index > 0 && column.priority !== undefined)
    .sort(
      (a, b) =>
        (a.column.priority ?? 0) - (b.column.priority ?? 0) ||
        // 같은 차례면 오른쪽 열이 먼저 빠진다 — 왼쪽일수록 줄을 읽는 데 가깝다
        b.index - a.index,
    )
    .map(({ column }) => column.key)
}

/** 지금 보일 열 — 빠지는 차례에서 앞 `hidden` 개를 뺀 나머지(원래 순서 그대로) */
export function visibleColumns<C extends FitColumn>(columns: readonly C[], hidden: number): C[] {
  if (hidden <= 0) return [...columns]
  const gone = new Set(hideOrder(columns).slice(0, hidden))
  return columns.filter((column) => !gone.has(column.key))
}

/**
 * 맞춤 상태.
 * - `hidden` — 빠진 열 수(빠지는 차례의 앞에서부터)
 * - `need[k]` — 열 `k` 개가 빠진 표가 넘치지 않으려면 필요한 폭(그때 잰 표의 최소 폭). 넘쳤을 때만 안다
 */
export interface FitState {
  hidden: number
  need: readonly (number | undefined)[]
}

export const FIT_INITIAL: FitState = { hidden: 0, need: [] }

/**
 * 한 번 재고 다음 상태를 정한다.
 *
 * @param available  표가 놓인 상자의 안쪽 폭
 * @param tableWidth 지금 그려진 표의 폭 — 넘칠 때는 그것이 곧 그 열 조합의 **최소 폭**이다
 *                   (`width:100%` 표는 최소 폭 아래로 줄지 않는다)
 * @param hideable   뺄 수 있는 열의 수(`hideOrder().length`)
 *
 * - 넘치면 하나 더 뺀다(그때의 표 폭을 `need` 에 적어 둔다 — 되돌릴 때 기준이다)
 * - 안 넘치고 빠진 열이 있으면, 마지막으로 뺀 열을 되살려도 들어가는지 `need` 로 본다.
 *   🔴 `need` 를 모르면(표 내용이 바뀌어 잊었다) **한 번 되살려 본다** — 넘치면 다음 번에 다시 빠지고
 *   그때 폭을 알게 되어 멈춘다. 이 왕복은 그리기 전(레이아웃 단계)에 끝나 화면에 깜빡이지 않는다
 * - 바뀔 게 없으면 **같은 객체**를 돌려준다(부르는 쪽이 다시 그리지 않게)
 */
export function nextFit(
  state: FitState,
  { available, tableWidth, hideable }: { available: number; tableWidth: number; hideable: number },
): FitState {
  const hidden = Math.min(state.hidden, hideable)
  if (hideable === 0) return state.hidden === 0 ? state : FIT_INITIAL

  if (tableWidth > available + FIT_TOLERANCE) {
    if (hidden >= hideable) return hidden === state.hidden ? state : { ...state, hidden }
    const need = [...state.need]
    need[hidden] = tableWidth
    return { hidden: hidden + 1, need }
  }

  if (hidden > 0) {
    const want = state.need[hidden - 1]
    if (want === undefined || want <= available + FIT_TOLERANCE) {
      return { hidden: hidden - 1, need: state.need }
    }
  }
  return hidden === state.hidden ? state : { ...state, hidden }
}

/**
 * **잰 때의 상태**로만 다음 상태를 정한다 — 같은 그림을 두 번 재어 두 단계를 가는 경합을 막는다.
 *
 * 🔴 쵸단 실측(2026-09-30 · 관리 › 사용자 서랍 1440×900): 한 그림(5열 · 표 563.7 > 상자 548.3)을 레이아웃 효과와
 *    `ResizeObserver` 가 **각각** 재어 `setState(prev => nextFit(prev, 같은 폭))` 이 두 번 쌓였다. 첫째가 1열을 빼고,
 *    둘째는 이미 1열이 빠진 `prev` 에 5열 때의 폭을 대어 **하나를 더 뺐다**(0→1→2). 게다가 `need[1]` 에 5열 폭이
 *    적혀 되살리기 조건이 영영 안 서 — 4열이면 들어가는데 3열로 굳었다.
 * ⇒ 잰 그림이 그려질 때의 `hidden` 과 `prev.hidden` 이 다르면 그 잰 값은 낡았다 — `prev` 를 그대로 돌려준다.
 *    새 그림이 그려지면 레이아웃 효과가 다시 잰다.
 */
export function nextFitMeasured(
  prev: FitState,
  measuredHidden: number,
  measure: { available: number; tableWidth: number; hideable: number },
): FitState {
  return prev.hidden !== measuredHidden ? prev : nextFit(prev, measure)
}

/** 표 내용이 바뀌었다 — 잰 폭을 잊는다(빠진 수는 그대로 두고 다음 재기에서 되살려 본다) */
export function forgetWidths(state: FitState): FitState {
  return state.need.length === 0 ? state : { hidden: state.hidden, need: [] }
}

/**
 * 폭 하나에 대해 끝까지 돌린 결과 — 시험과 설명용. `widthOf(hidden)` 는 그 열 조합의 최소 폭.
 * 실제 화면은 이 루프를 `DataTable` 의 레이아웃 효과가 한 단계씩 돈다.
 */
export function settle(
  available: number,
  widthOf: (hidden: number) => number,
  hideable: number,
  from: FitState = FIT_INITIAL,
): FitState {
  let state = from
  for (let guard = 0; guard < (hideable + 1) * 4; guard += 1) {
    const tableWidth = Math.max(available, widthOf(state.hidden))
    const next = nextFit(state, { available, tableWidth, hideable })
    if (next === state) return state
    state = next
  }
  return state
}
